import {
  Backdrop,
  Box,
  Button,
  ClickAwayListener,
  Icon,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFlag } from '@schooly/hooks/use-flag';
import { MouseEvent, ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { ArrowDownV2Icon, CheckIcon, CrossCircleIcon } from '../assets/assets';

export interface SimpleDropdownProps<T> {
  onChange: (v: T) => void;
  currentValue?: T;
  options: T[];
  defaultValue?: T;
  disabled?: boolean;
  color?: string;
  renderCustomLabel?: (opened: boolean, open: () => void, data?: T) => ReactNode;
}

export const SimpleDropdown = <T extends { name: string }>({
  onChange,
  currentValue,
  defaultValue,
  options,
  disabled,
  color,
  renderCustomLabel,
}: SimpleDropdownProps<T>) => {
  const [opened, open, close] = useFlag();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [buttonWidth, setButtonWidth] = useState<number>();

  const handleChange = (value: T) => {
    onChange(value);
    close();
  };

  const handleClear = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      e.stopPropagation();
      if (!defaultValue) return;
      onChange(defaultValue);
      close();
    },
    [close, defaultValue, onChange],
  );

  const currentColor = color ?? 'text.primary';

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useLayoutEffect(() => {
    if (!opened && buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, [opened, currentValue?.name]);

  return (
    <>
      <Backdrop open={opened} invisible sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} />
      <ClickAwayListener onClickAway={close}>
        <div>
          <Tooltip
            PopperProps={{
              disablePortal: true,
            }}
            onClose={close}
            open={opened}
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: (theme) => ({
                  width: 121,
                  maxWidth: 121,
                  borderRadius: theme.spacing(1),
                  border: `1px solid ${theme.palette.common.light3}`,
                  padding: 0,
                  overflow: 'hidden',
                  margin: `${theme.spacing(0.25, 0, 0)} !important`,
                }),
              },
            }}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  mt: 1,
                  mb: 0.75,
                  px: 1,
                }}
              >
                {options.map((option) => {
                  const isSelected = currentValue?.name === option.name;
                  return (
                    <MenuItem
                      key={option.name}
                      onClick={() => handleChange(option)}
                      sx={(theme) => ({
                        m: 0,
                        py: theme.spacing(0.25),
                        pl: 0.25,
                        pr: 0.5,
                        height: 24,
                        borderRadius: theme.spacing(0.5),
                        width: '100%',
                        '&:hover': {
                          '.MuiTypography-root': {
                            color: (theme) => theme.palette.primary.main,
                          },
                        },
                      })}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Box ml={0.25}>
                          <Typography
                            variant="h3"
                            color={isSelected ? 'primary.main' : currentColor}
                          >
                            {option.name}
                          </Typography>
                        </Box>
                        {isSelected && (
                          <Icon
                            sx={{
                              color: 'primary.main',
                            }}
                          >
                            <CheckIcon />
                          </Icon>
                        )}
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Stack>
            }
          >
            <Box>
              {renderCustomLabel ? (
                renderCustomLabel(opened, open, currentValue)
              ) : (
                <Button
                  ref={buttonRef}
                  sx={(theme) => ({
                    px: '8px !important',
                    py: '2px !important',
                    height: 30,
                    width: buttonWidth,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiTypography-root, & .MuiIcon-root': {
                      color: opened ? 'background.paper' : currentColor,
                    },

                    '&:hover .MuiTypography-root, &:hover .svg-icon path': {
                      color: theme.palette.primary.main,
                      stroke: theme.palette.primary.main,
                    },
                  })}
                  size="small"
                  variant={opened ? 'contained' : 'text'}
                  endIcon={
                    <Box
                      sx={(theme) => ({
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: theme.spacing(1.5),
                        // pr: 0.25,
                      })}
                    >
                      {!opened ? (
                        <Icon
                          sx={(theme) => ({
                            width: theme.spacing(1),
                          })}
                        >
                          <ArrowDownV2Icon />
                        </Icon>
                      ) : (
                        <Icon
                          sx={(theme) => ({
                            width: theme.spacing(1.5),
                          })}
                          onClick={handleClear}
                        >
                          <CrossCircleIcon />
                        </Icon>
                      )}
                    </Box>
                  }
                  onClick={open}
                >
                  <Typography
                    variant="h3"
                    color="inherit"
                    gap={1}
                    onClick={!disabled ? open : undefined}
                  >
                    {currentValue?.name}
                  </Typography>
                </Button>
              )}
            </Box>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </>
  );
};
